/**
 * ActivityFeedPageController — page dédiée `/activite` (EPIC-029 / 29.5 — §15.C).
 *
 * Liste paginée (25/page) du flux d'activité, filtres combinables
 * persistés en querystring (domaine multi-sélection, période avec
 * raccourcis, ressource liée), synchronisation bidirectionnelle URL ↔
 * état (popstate restaure les filtres précédents).
 *
 * Verbatim maquette `activite.html` (sélecteurs `nm-timeline__when` /
 * `__what` / `__who`, `nm-pagination` du DS partagé). Mapping
 * `eventType` + `metadata` → `label` / `href` / `details` factorisé
 * dans `utils/activity_feed_mapping.js` (partagé avec 29.4).
 *
 * États visibles : `loading` au connect / changement de filtre, `list`
 * (≥1 événement), `empty` (0 événement OU erreur réseau hors 401 —
 * message neutre, jamais d'erreur technique côté UI). 401 reste géré
 * globalement par `api.js`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { buildEventMapping } from "/js/utils/activity_feed_mapping.js";
import { formatAbsoluteDate } from "/js/utils/date_absolute.js";

const DEFAULT_RANGE_DAYS = 30;

export default class ActivityFeedPageController extends BaseController {
    static targets = [
        "domainCheckbox",
        "startDate",
        "endDate",
        "loading",
        "list",
        "pagination",
        "empty",
        "itemTemplate",
        "pageButtonTemplate",
    ];
    static values = {
        perPage: { type: Number, default: 25 },
    };

    connect() {
        this.popstateHandler = () => {
            this.restoreFiltersFromUrl();
            this.reflectFiltersInDom();
            this.loadFeed();
        };
        window.addEventListener("popstate", this.popstateHandler);

        this.restoreFiltersFromUrl();
        this.reflectFiltersInDom();
        this.loadFeed();
    }

    disconnect() {
        if (this.popstateHandler) {
            window.removeEventListener("popstate", this.popstateHandler);
        }
    }

    // -------------------------------------------------------------------------
    // État filtres ↔ URL
    // -------------------------------------------------------------------------

    restoreFiltersFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const defaults = defaultDateRange();

        const domainCsv = params.get("domain") ?? "";
        this.filters = {
            domain: domainCsv ? domainCsv.split(",").filter(Boolean) : [],
            startDate: params.get("startDate") ?? defaults.startDate,
            endDate: params.get("endDate") ?? defaults.endDate,
            clientId: params.get("clientId") ?? "",
            invoiceId: params.get("invoiceId") ?? "",
            quoteId: params.get("quoteId") ?? "",
            page: Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1),
        };
    }

    reflectFiltersInDom() {
        for (const checkbox of this.domainCheckboxTargets) {
            checkbox.checked = this.filters.domain.includes(checkbox.value);
        }
        this.startDateTarget.value = this.filters.startDate;
        this.endDateTarget.value = this.filters.endDate;
    }

    updateUrl() {
        const params = new URLSearchParams();
        if (this.filters.domain.length) params.set("domain", this.filters.domain.join(","));
        if (this.filters.startDate) params.set("startDate", this.filters.startDate);
        if (this.filters.endDate) params.set("endDate", this.filters.endDate);
        if (this.filters.clientId) params.set("clientId", this.filters.clientId);
        if (this.filters.invoiceId) params.set("invoiceId", this.filters.invoiceId);
        if (this.filters.quoteId) params.set("quoteId", this.filters.quoteId);
        if (this.filters.page > 1) params.set("page", String(this.filters.page));

        const query = params.toString();
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
        window.history.pushState(null, "", url);
    }

    // -------------------------------------------------------------------------
    // Actions (DOM → état)
    // -------------------------------------------------------------------------

    onFilterChange() {
        this.filters.domain = this.domainCheckboxTargets
            .filter((cb) => cb.checked)
            .map((cb) => cb.value);
        this.filters.startDate = this.startDateTarget.value;
        this.filters.endDate = this.endDateTarget.value;
        // clientId / invoiceId / quoteId : filtres pilotés par l'URL uniquement
        // (deep-link depuis une fiche), sans champ de saisie — préservés tels quels.
        this.filters.page = 1;

        this.updateUrl();
        this.loadFeed();
    }

    applyShortcut(event) {
        const button = event.currentTarget;
        const range = computeShortcutRange(button.dataset);
        if (!range) return;

        this.filters.startDate = range.startDate;
        this.filters.endDate = range.endDate;
        this.startDateTarget.value = range.startDate;
        this.endDateTarget.value = range.endDate;
        this.filters.page = 1;

        this.updateUrl();
        this.loadFeed();
    }

    resetFilters(event) {
        if (event && typeof event.preventDefault === "function") {
            event.preventDefault();
        }
        const defaults = defaultDateRange();
        this.filters = {
            domain: [],
            startDate: defaults.startDate,
            endDate: defaults.endDate,
            clientId: "",
            invoiceId: "",
            quoteId: "",
            page: 1,
        };
        this.reflectFiltersInDom();
        this.updateUrl();
        this.loadFeed();
    }

    goToPage(event) {
        const button = event.currentTarget;
        const page = parseInt(button.dataset.page ?? "0", 10);
        if (!Number.isFinite(page) || page < 1) return;

        this.filters.page = page;
        this.updateUrl();
        this.loadFeed();
    }

    // -------------------------------------------------------------------------
    // Appel API + rendu
    // -------------------------------------------------------------------------

    async loadFeed() {
        this.showLoading();
        try {
            const response = await api.activityFeed.list(this.buildRequestParams());
            const events = Array.isArray(response?.data) ? response.data : [];
            const pagination = response?.pagination ?? null;

            if (events.length === 0) {
                this.showEmpty(pagination);

                return;
            }
            this.renderItems(events);
            this.renderPagination(pagination);
            this.showList();
        } catch {
            // 401 déjà géré globalement par api.js (redirect /connexion.html).
            // Toute autre erreur → état vide neutre (pas de message
            // technique côté UI utilisateur).
            this.showEmpty(null);
        }
    }

    buildRequestParams() {
        const params = {
            page: this.filters.page,
            perPage: this.perPageValue,
            sort: "-occurredAt",
        };
        if (this.filters.domain.length) {
            params["filter[domain]"] = this.filters.domain.join(",");
        }
        if (this.filters.startDate) params["filter[startDate]"] = this.filters.startDate;
        if (this.filters.endDate) params["filter[endDate]"] = this.filters.endDate;
        if (this.filters.clientId) params["filter[clientId]"] = this.filters.clientId;
        if (this.filters.invoiceId) params["filter[invoiceId]"] = this.filters.invoiceId;
        if (this.filters.quoteId) params["filter[quoteId]"] = this.filters.quoteId;

        return params;
    }

    renderItems(events) {
        this.listTarget.replaceChildren();
        for (const event of events) {
            const fragment = this.itemTemplateTarget.content.cloneNode(true);
            this.fillEvent(fragment, event);
            this.listTarget.appendChild(fragment);
        }
    }

    fillEvent(fragment, event) {
        const dateEl = fragment.querySelector('[data-slot="dateAbsolute"]');
        const linkEl = fragment.querySelector('[data-slot="link"]');
        const labelEl = fragment.querySelector('[data-slot="label"]');
        const detailsEl = fragment.querySelector('[data-slot="details"]');
        const domainEl = fragment.querySelector('[data-slot="domainLabel"]');

        const mapping = buildEventMapping(event);
        const occurredAt = event?.occurredAt ?? "";

        if (dateEl) {
            dateEl.textContent = formatAbsoluteDate(occurredAt);
            if (occurredAt) dateEl.setAttribute("title", occurredAt);
        }
        if (labelEl) labelEl.textContent = mapping.label;
        if (linkEl) {
            if (mapping.href) {
                linkEl.setAttribute("href", mapping.href);
            } else {
                linkEl.removeAttribute("href");
            }
        }
        if (detailsEl) {
            if (mapping.details) {
                detailsEl.textContent = ` · ${mapping.details}`;
            } else {
                detailsEl.remove();
            }
        }
        if (domainEl) domainEl.textContent = mapping.domainLabel;
    }

    renderPagination(pagination) {
        const captionEl = this.paginationTarget.querySelector("span");
        const pagesEl = this.paginationTarget.querySelector(".nm-pagination__pages");
        if (!captionEl || !pagesEl) return;

        const totalPages = pagination?.totalPages ?? 1;
        const total = pagination?.total ?? 0;
        const page = pagination?.page ?? this.filters.page;
        const perPage = pagination?.perPage ?? this.perPageValue;
        const displayed = Math.min(perPage, total - (page - 1) * perPage);

        captionEl.textContent = formatPaginationCaption(displayed, total);
        pagesEl.replaceChildren();

        if (totalPages <= 1) return;

        pagesEl.appendChild(this.buildPageButton("«", page > 1 ? page - 1 : null, "Page précédente"));
        for (const entry of this.getPagesToShow(page, totalPages)) {
            if (entry === "...") {
                pagesEl.appendChild(this.buildPageButton("…", null, "Pages intermédiaires"));
            } else {
                const btn = this.buildPageButton(String(entry), entry, `Page ${entry}`);
                if (entry === page) btn.classList.add("is-current");
                pagesEl.appendChild(btn);
            }
        }
        pagesEl.appendChild(
            this.buildPageButton("»", page < totalPages ? page + 1 : null, "Page suivante"),
        );
    }

    buildPageButton(label, page, title) {
        const fragment = this.pageButtonTemplateTarget.content.cloneNode(true);
        const button = fragment.querySelector("button");
        button.textContent = label;
        button.setAttribute("title", title);
        if (page === null) {
            button.disabled = true;
        } else {
            button.dataset.page = String(page);
            button.dataset.action = "click->activity-feed-page#goToPage";
        }

        return button;
    }

    // -------------------------------------------------------------------------
    // Bascule visibilité (loading / list / empty)
    // -------------------------------------------------------------------------

    showLoading() {
        this.loadingTarget.hidden = false;
        this.listTarget.hidden = true;
        this.paginationTarget.hidden = true;
        this.emptyTarget.hidden = true;
    }

    showList() {
        this.loadingTarget.hidden = true;
        this.listTarget.hidden = false;
        this.paginationTarget.hidden = false;
        this.emptyTarget.hidden = true;
    }

    showEmpty(pagination) {
        this.loadingTarget.hidden = true;
        this.listTarget.hidden = true;
        this.paginationTarget.hidden = true;
        this.emptyTarget.hidden = false;
        // Garde le compteur cohérent avec la pagination retournée
        if (pagination) {
            this.renderPagination(pagination);
        }
    }
}

// -----------------------------------------------------------------------------
// Helpers purs
// -----------------------------------------------------------------------------

function defaultDateRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - DEFAULT_RANGE_DAYS);

    return {
        startDate: formatDateLocal(start),
        endDate: formatDateLocal(end),
    };
}

function computeShortcutRange(dataset) {
    if (dataset.days) {
        const days = parseInt(dataset.days, 10);
        if (!Number.isFinite(days) || days <= 0) return null;
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);

        return { startDate: formatDateLocal(start), endDate: formatDateLocal(end) };
    }
    if (dataset.preset === "year") {
        const now = new Date();
        const year = now.getFullYear();

        return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    }

    return null;
}

function formatDateLocal(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
}

function formatPaginationCaption(displayed, total) {
    if (total === 0) return "0 événement";

    return `${displayed} / ${total} événement${total > 1 ? "s" : ""}`;
}
